/* BOXZY progressive enhancement. No commerce requests or permanent render loop. */
(() => {
  if (window.boxzyMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 801px)');
  const enrolled = new WeakSet();
  const observed = new Set();
  const pointerNodes = new Set();
  const personalityGroups = new WeakSet();
  let frame = 0;
  let pendingPointer;
  let reactionTimer;
  let shippingUnlocked;
  const enabled = () => document.body.dataset.boxzyMotion !== 'false' && !reduced.matches;
  const scale = () => document.body.dataset.boxzyIntensity === 'standard' && !document.body.classList.contains('low-motion') ? 1 : 0.65;
  const allowed = el => enabled() && (!el.matches('[data-boxzy-reveal], [data-boxzy-vault], [data-boxzy-bounce]') || (!el.closest('[data-boxzy-reveal-scope]') || el.closest('[data-boxzy-reveal-scope]').hasAttribute('data-boxzy-reveals')));
  const all = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];

  function reveal(el, immediate = false) {
    if (immediate) el.classList.add('reveal-settled');
    el.classList.add('is-revealed');
    el.classList.remove('is-reveal-pending');
    if (el.hasAttribute('data-boxzy-reveal')) { observer?.unobserve(el); observed.delete(el); }
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const {target, isIntersecting} of entries) {
      if (target.matches('[data-cinema]')) {
        target.classList.toggle('is-in-view', isIntersecting);
        syncCinema(target);
      } else if (target.matches('[data-boxzy-float], .announcement')) {
        target.classList.toggle('is-in-view', isIntersecting);
      } else if (isIntersecting && (!document.hidden || !target.hasAttribute('data-boxzy-reveal'))) {
        reveal(target);
        observer.unobserve(target);
        observed.delete(target);
      }
    }
  }, {threshold: 0.12, rootMargin: '0px 0px -6% 0px'}) : null;

  function observe(el) {
    if (enrolled.has(el)) return;
    enrolled.add(el);
    if (el.matches('[data-cinema]')) { observed.add(el); if(observer)observer.observe(el); syncCinema(el); return; }
    if (!observer || !allowed(el)) { reveal(el, true); return; }
    // Restored pages must not hide content already above the current viewport.
    if (el.hasAttribute('data-boxzy-reveal') && el.getBoundingClientRect().bottom < 0) { reveal(el, true); return; }
    if (el.matches('[data-boxzy-reveal]')) el.classList.add('is-reveal-pending');
    observed.add(el);
    observer.observe(el);
  }
  function selectPersonality(card, focus = false) {
    const group = card.closest('[data-boxzy-personalities]');
    group.classList.add('has-selection');
    group.querySelectorAll('[data-personality-card]').forEach(item => {
      const selected = item === card;
      item.classList.toggle('is-selected', selected);
      item.querySelector('[data-personality-select]').setAttribute('aria-pressed', String(selected));
    });
    if (focus) card.querySelector('[data-personality-select]').focus();
  }
  function init(scope = document) {
    // Bounded card staggering; never target product media or purchase controls individually.
    all(scope, '.product-grid').forEach(grid => {
      [...grid.children].filter(card => card.matches('.product-card')).forEach((card, i) => {
        card.setAttribute('data-boxzy-reveal', 'card');
        card.style.setProperty('--reveal-delay', `${Math.min(i, 4) * 50}ms`);
      });
    });
    all(scope, '[data-boxzy-reveal], [data-boxzy-bounce], [data-boxzy-vault], [data-boxzy-float], [data-boxzy-peek], [data-cinema], .announcement').forEach(observe);
    all(scope, '[data-boxzy-personalities]').forEach(group => {
      if (personalityGroups.has(group)) return;
      personalityGroups.add(group);
      group.classList.add('personality-enhanced');
      const first = group.querySelector('[data-personality-card]');
      if (first) selectPersonality(first);
    });
    // First render records the state. Animate only a genuine locked -> unlocked update.
    const shipping = scope.querySelector('.boxzy-shipping');
    if (shipping) {
      const unlocked = shipping.hasAttribute('data-shipping-unlocked');
      if (unlocked && shippingUnlocked === false && enabled()) shipping.classList.add('just-unlocked');
      shippingUnlocked = unlocked;
    }
  }

  function syncCinema(scene) {
    const video=scene.querySelector('video');const control=scene.querySelector('[data-cinema-toggle]');
    if(!video)return;
    const playable=enabled() && finePointer.matches && !document.hidden && scene.classList.contains('is-in-view') && scene.dataset.userPaused!=='true';
    if(!playable)video.pause();
    else video.play().catch(()=>{if(control)control.textContent='Play video';});
    if(control){control.setAttribute('aria-pressed',String(!playable));control.textContent=playable?'Pause video':'Play video';}
  }
  function resetPointer(el) {
    ['--pointer-x','--pointer-y','--tilt-x','--tilt-y','--pointer-angle'].forEach(name => el.style.removeProperty(name));
    el.classList.remove('is-pointer-active');
    pointerNodes.delete(el);
  }
  function stopPointer() {
    cancelAnimationFrame(frame);
    frame = 0;
    pendingPointer = null;
    [...pointerNodes].forEach(resetPointer);
  }
  function syncPreferences() {
    document.body.classList.toggle('boxzy-motion-active', enabled());
    document.body.classList.toggle('boxzy-motion-off', !enabled());
    stopPointer();
    document.querySelectorAll('[data-cinema]').forEach(syncCinema);
    if (enabled()) {
      document.querySelectorAll('[data-boxzy-float], .announcement').forEach(el => { if (allowed(el) && observer) { observed.add(el); observer.observe(el); } });
    } else {
      observed.forEach(el => { reveal(el, true); if(!el.matches('[data-cinema]')) {observer?.unobserve(el);observed.delete(el);} });
    }
  }
  document.addEventListener('pointermove', event => {
    if (!enabled() || !finePointer.matches || event.pointerType === 'touch') return;
    const el = event.target.closest('[data-boxzy-tilt], [data-boxzy-magnetic], [data-boxzy-parallax]');
    if (!el) return;
    pendingPointer = {el, x: event.clientX, y: event.clientY};
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const current = pendingPointer;
      pendingPointer = null;
      if (!current || !current.el.isConnected || !enabled() || !finePointer.matches) return;
      const {el, x, y} = current;
      const rect = el.getBoundingClientRect();
      const nx = Math.max(-1, Math.min(1, (x - rect.left) / Math.max(1, rect.width) * 2 - 1));
      const ny = Math.max(-1, Math.min(1, (y - rect.top) / Math.max(1, rect.height) * 2 - 1));
      const intensity = scale();
      if (el.hasAttribute('data-boxzy-tilt')) {
        el.style.setProperty('--tilt-x', `${-ny * 2 * intensity}deg`);
        el.style.setProperty('--tilt-y', `${nx * 2 * intensity}deg`);
      } else {
        const max = el.hasAttribute('data-boxzy-magnetic') ? 4 : 12;
        el.style.setProperty('--pointer-x', `${nx * max * intensity}px`);
        el.style.setProperty('--pointer-y', `${ny * max * intensity}px`);
        if (max === 12) el.style.setProperty('--pointer-angle', `${nx * 2 * intensity}deg`);
      }
      el.classList.add('is-pointer-active');
      pointerNodes.add(el);
    });
  }, {passive: true});
  document.addEventListener('pointerout', event => {
    const el = event.target.closest('[data-boxzy-tilt], [data-boxzy-magnetic], [data-boxzy-parallax]');
    if (el && !el.contains(event.relatedTarget)) {
      if (pendingPointer?.el === el) pendingPointer = null;
      resetPointer(el);
    }
  });
  document.addEventListener('pointerover', event => {
    if (!finePointer.matches || event.pointerType === 'touch') return;
    const card = event.target.closest('[data-personality-card]');
    if (card && !card.contains(event.relatedTarget)) selectPersonality(card);
  });
  document.addEventListener('click', event => {
    const control=event.target.closest('[data-cinema-toggle]');
    if(control){const scene=control.closest('[data-cinema]');const video=scene.querySelector('video');scene.dataset.userPaused=String(!video.paused);syncCinema(scene);}
    const card = event.target.closest('[data-personality-card]');
    if (card && !event.target.closest('a')) selectPersonality(card);
  });
  document.addEventListener('focusin', event => {
    const pending = event.target.closest('.is-reveal-pending');
    if (pending) reveal(pending, true);
    const card = event.target.closest('[data-personality-card]');
    if (card) selectPersonality(card);
  });
  document.addEventListener('keydown', event => {
    if (!event.target.matches('[data-personality-select]')) return;
    const keys = ['ArrowLeft','ArrowRight','Home','End'];
    if (!keys.includes(event.key)) return;
    const cards = [...event.target.closest('[data-boxzy-personalities]').querySelectorAll('[data-personality-card]')];
    const index = cards.indexOf(event.target.closest('[data-personality-card]'));
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + cards.length) % cards.length;
    event.preventDefault(); selectPersonality(cards[next], true);
  });
  document.addEventListener('boxzy:cart-added', () => {
    const reaction = document.querySelector('[data-cart-reaction]');
    if (!reaction) return;
    clearTimeout(reactionTimer);
    reaction.hidden = false;
    reactionTimer = setTimeout(() => { reaction.hidden = true; }, 1000);
  });
  document.addEventListener('animationend', event => {
    if (event.animationName === 'boxzy-text-reveal') event.target.classList.add('reveal-settled');
  });
  // BFCache restore keeps visited content visible rather than replaying its entrance.
  window.addEventListener('pageshow', event => {
    if (event.persisted) document.querySelectorAll('[data-boxzy-reveal]').forEach(el => reveal(el, true));
  });
  document.addEventListener('shopify:section:select', event => {
    all(event.target, '[data-boxzy-reveal]').forEach(el => reveal(el, true));
  });
  document.addEventListener('boxzy:content-updated' , event => init(event.detail?.scope || document));
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    observed.forEach(el => { if (event.target.contains(el)) { el.querySelector?.('video')?.pause(); observer?.unobserve(el); observed.delete(el); } });
    pointerNodes.forEach(el => { if (event.target.contains(el)) resetPointer(el); });
    if (pendingPointer && event.target.contains(pendingPointer.el)) stopPointer();
  });
  document.addEventListener('shopify:block:select', event => {
    const card = event.target.closest('[data-personality-card]');
    if (card) selectPersonality(card);
  });
  document.addEventListener('visibilitychange', () => {
    document.body.classList.toggle('boxzy-motion-paused', document.hidden);
    if (document.hidden) stopPointer();
    else observed.forEach(el => {
      if (!el.hasAttribute('data-boxzy-reveal')) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < innerHeight * .94 && rect.bottom > 0) reveal(el);
    });
    document.querySelectorAll('[data-cinema]').forEach(syncCinema);
  });
  reduced.addEventListener('change', syncPreferences);
  finePointer.addEventListener('change', () => {stopPointer();document.querySelectorAll('[data-cinema]').forEach(syncCinema);});
  window.boxzyMotion = {init};
  syncPreferences();
  init();
})();
